// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import moment from 'moment';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateBackorderMutation from 'main-app/mutations/UpdateBackorder';
import CloseBackorderMutation from 'main-app/mutations/CloseBackorder';
import EmailBackorderMutation from 'main-app/mutations/EmailBackorder';
import BackorderStatusPill from 'shared/components/common/Pill/BackorderStatusPill';
import type { RouterHistory, Match } from 'react-router';

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

const TdRight = styled(Td)`
  text-align: right;
`;

const LineStatusPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => (props.fulfilled ? '#e6fffb' : '#fff7e6')};
  color: ${props => (props.fulfilled ? '#13c2c2' : '#fa8c16')};
`;

type Props = {
  history: RouterHistory,
  match: Match,
};

type State = {};

class Backorder extends PureComponent<Props, State> {
  state = {};

  handleClose = (backorderId: string) => {
    CloseBackorderMutation.commit({
      variables: { input: { id: backorderId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Backorder closed.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error closing backorder.'), 'error');
      });
  };

  handleEmailBackorder = (backorderId: string) => {
    EmailBackorderMutation.commit({
      variables: { input: { backorderId } },
    })
      .then(() => {
        Actions.alertNotification(
          i18n.t('Backorder notification sent.'),
          'success',
        );
      })
      .catch(() => {
        Actions.alertNotification(
          i18n.t('Error sending backorder email.'),
          'error',
        );
      });
  };

  handleCreateShipment = (backorder: Object) => {
    const { history } = this.props;
    history.push(
      `/shipments?orderId=${backorder.order.id}`,
    );
  };

  render() {
    const { match, history } = this.props;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query BackorderQuery($id: ID) {
            backorder(id: $id) {
              id
              backorderNumber
              status
              expectedDate
              notes
              order {
                id
                orderNumber
              }
              customer {
                id
                name
              }
              lines(first: 1000) {
                edges {
                  node {
                    id
                    orderedQuantity
                    shippedQuantity
                    backorderedQuantity
                    fulfilledQuantity
                    status
                    item {
                      id
                      name
                      itemNumber
                    }
                    createdAt
                  }
                }
              }
              updatedAt
              createdAt
            }
            viewer {
              id
              permissions
            }
          }
        `}
        variables={{ id: match.params.id }}
        render={({ props }) => {
          if (!props) return <Page><Loader /></Page>;

          const { backorder, viewer } = props;
          const lineEdges = backorder.lines?.edges || [];
          const isOpen =
            backorder.status === 'OPEN' ||
            backorder.status === 'PARTIALLY_FULFILLED';

          const contextMenuItems = [];
          if (isOpen) {
            contextMenuItems.push({
              label: i18n.t('Close Backorder'),
              onClick: () => this.handleClose(backorder.id),
              destructive: true,
            });
          }

          return (
            <Page>
              <PageHeader
                title={`${i18n.t('Backorder')} ${backorder.backorderNumber}`}
                backLabel={i18n.t('Backorders')}
                backTo="/backorders"
                contextMenuItems={contextMenuItems}
              />
              <Content>
                <ActionBar>
                  {isOpen && (
                    <Button
                      theme="blue"
                      onClick={() =>
                        this.handleCreateShipment(backorder)
                      }
                    >
                      {i18n.t('Create Fulfillment Shipment')}
                    </Button>
                  )}
                  <Button
                    theme="blue"
                    onClick={() =>
                      this.handleEmailBackorder(backorder.id)
                    }
                  >
                    {i18n.t('Email Notification')}
                  </Button>
                  {isOpen && (
                    <Button
                      theme="border-grey"
                      onClick={() => this.handleClose(backorder.id)}
                    >
                      {i18n.t('Close Backorder')}
                    </Button>
                  )}
                </ActionBar>

                <Section>
                  <SectionTitle>{i18n.t('Details')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>
                        {i18n.t('Backorder #')}
                      </DetailLabel>
                      <DetailValue>
                        {backorder.backorderNumber}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Order')}</DetailLabel>
                      <DetailValue>
                        <a
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            history.push(
                              `/order/${backorder.order.id}`,
                            );
                          }}
                        >
                          {backorder.order.orderNumber}
                        </a>
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Customer')}</DetailLabel>
                      <DetailValue>
                        {backorder.customer.name}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Status')}</DetailLabel>
                      <DetailValue>
                        <BackorderStatusPill
                          status={backorder.status}
                        />
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>
                        {i18n.t('Expected Date')}
                      </DetailLabel>
                      <DetailValue>
                        {backorder.expectedDate
                          ? moment(backorder.expectedDate).format(
                              'MM/DD/YYYY',
                            )
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Created')}</DetailLabel>
                      <DetailValue>
                        {moment(backorder.createdAt).format(
                          'MM/DD/YYYY',
                        )}
                      </DetailValue>
                    </DetailItem>
                    {backorder.notes && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Notes')}</DetailLabel>
                        <DetailValue>{backorder.notes}</DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>
                    {i18n.t('Backorder Lines')}
                  </SectionTitle>
                  <ItemsTable>
                    <thead>
                      <tr>
                        <Th>{i18n.t('Item')}</Th>
                        <Th>{i18n.t('Item #')}</Th>
                        <ThRight>{i18n.t('Ordered')}</ThRight>
                        <ThRight>{i18n.t('Shipped')}</ThRight>
                        <ThRight>{i18n.t('Backordered')}</ThRight>
                        <ThRight>{i18n.t('Fulfilled')}</ThRight>
                        <Th>{i18n.t('Status')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineEdges.map(edge => {
                        const line = edge.node;
                        return (
                          <tr key={line.id}>
                            <Td>{line.item.name}</Td>
                            <Td>{line.item.itemNumber}</Td>
                            <TdRight>
                              {line.orderedQuantity}
                            </TdRight>
                            <TdRight>
                              {line.shippedQuantity}
                            </TdRight>
                            <TdRight>
                              {line.backorderedQuantity}
                            </TdRight>
                            <TdRight>
                              {line.fulfilledQuantity}
                            </TdRight>
                            <Td>
                              <LineStatusPill
                                fulfilled={
                                  line.status === 'FULFILLED'
                                }
                              >
                                {line.status === 'FULFILLED'
                                  ? i18n.t('Fulfilled')
                                  : i18n.t('Open')}
                              </LineStatusPill>
                            </Td>
                          </tr>
                        );
                      })}
                      {lineEdges.length === 0 && (
                        <tr>
                          <Td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>
                            {i18n.t('No backorder lines')}
                          </Td>
                        </tr>
                      )}
                    </tbody>
                  </ItemsTable>
                </Section>
              </Content>
            </Page>
          );
        }}
      />
    );
  }
}

export default Backorder;
